import React, { useEffect, useState } from 'react';
import { Shimmer } from '@fluentui/react';
import GeneralService from '../../../../../services/general';
import VendorsService from '../../../../../services/data/vendors';
import PermissionsService from '../../../../../services/permissions';
import { useStore } from '../../../../../stores/root';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxes, faMoneyBillWave, faCheckCircle, faClock } from '@fortawesome/pro-light-svg-icons';

interface IVendorSummaryDashboardProps {
    vendorId: string;
    onCardClick?: (target: 'orders' | 'expenses_all' | 'expenses_paid' | 'expenses_unpaid') => void;
}

const VendorSummaryDashboard: React.FC<IVendorSummaryDashboardProps> = ({ vendorId, onCardClick }) => {
    const { user } = useStore();
    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);

        VendorsService.summary(vendorId)
            .then((data) => {
                if (isMounted) {
                    const actualData = (data && data.data !== undefined) ? data.data : data;
                    setSummary(actualData || {});
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error("Failed to load vendor summary:", err);
                if (isMounted) {
                    setSummary({});
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [vendorId]);

    const s = summary || {};
    const totalOrders = s.total_orders ?? s.totalOrders ?? 0;
    const totalExpenses = s.total_expenses ?? s.totalExpenses ?? 0;
    const totalExpensesPaid = s.total_expenses_paid ?? s.totalExpensesPaid ?? 0;
    const totalExpensesUnpaid = s.total_expenses_unpaid ?? s.totalExpensesUnpaid ?? 0;

    const userHasOrderPermission = (
        PermissionsService.hasPermission(['orders.read.all'], user.permissions) ||
        PermissionsService.hasPermission(['superuser', 'superadmin'], user.roles)
    );

    const userHasExpensePermission = (
        PermissionsService.hasPermission(['orders.expenses.read.all', 'outcomes.invoices.read.all'], user.permissions) ||
        PermissionsService.hasPermission(['superuser', 'superadmin'], user.roles)
    );

    const canViewOrders = userHasOrderPermission && (s.can_read_orders === undefined || s.can_read_orders === true);
    const canViewExpenses = userHasExpensePermission && (s.can_read_expenses === undefined || s.can_read_expenses === true);

    const allCards: {
        title: string;
        value: string;
        icon: any;
        color: string;
        bg: string;
        isExpense: boolean;
        target: 'orders' | 'expenses_all' | 'expenses_paid' | 'expenses_unpaid';
        hint: string;
    }[] = [
        {
            title: 'Order Jalan',
            value: `${totalOrders} Order`,
            icon: faBoxes,
            color: '#2563eb',
            bg: '#eff6ff',
            isExpense: false,
            target: 'orders',
            hint: 'Lihat daftar order →'
        },
        {
            title: 'Total Expense',
            value: `Rp. ${GeneralService.getNumberWithSeparator(String(totalExpenses), '.')}`,
            icon: faMoneyBillWave,
            color: '#4f46e5',
            bg: '#eef2ff',
            isExpense: true,
            target: 'expenses_all',
            hint: 'Lihat semua invoice →'
        },
        {
            title: 'Expense Paid',
            value: `Rp. ${GeneralService.getNumberWithSeparator(String(totalExpensesPaid), '.')}`,
            icon: faCheckCircle,
            color: '#16a34a',
            bg: '#f0fdf4',
            isExpense: true,
            target: 'expenses_paid',
            hint: 'Lihat invoice lunas →'
        },
        {
            title: 'Expense Unpaid',
            value: `Rp. ${GeneralService.getNumberWithSeparator(String(totalExpensesUnpaid), '.')}`,
            icon: faClock,
            color: '#dc2626',
            bg: '#fef2f2',
            isExpense: true,
            target: 'expenses_unpaid',
            hint: 'Lihat invoice belum lunas →'
        },
    ];

    const cards = allCards.filter(c => {
        if (c.target === 'orders') return canViewOrders;
        if (c.isExpense) return canViewExpenses;
        return false;
    });

    if (cards.length === 0) {
        return null;
    }

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: cards.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
            gap: '12px',
            width: '100%'
        }}>
            {cards.map((card, idx) => {
                const isHovered = hoveredIdx === idx;
                const isClickable = Boolean(onCardClick);

                return (
                    <div
                        key={idx}
                        onClick={() => {
                            if (isClickable && onCardClick) {
                                onCardClick(card.target);
                            }
                        }}
                        onMouseEnter={() => setHoveredIdx(idx)}
                        onMouseLeave={() => setHoveredIdx(null)}
                        style={{
                            background: card.bg,
                            border: `1px solid ${isHovered ? card.color : `${card.color}35`}`,
                            borderRadius: '8px',
                            padding: '12px 16px',
                            boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.03)',
                            transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                            transition: 'all 0.18s ease-in-out',
                            cursor: isClickable ? 'pointer' : 'default',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 600 }}>{card.title}</span>
                            <FontAwesomeIcon icon={card.icon} style={{ color: card.color, fontSize: '14px' }} />
                        </div>
                        {loading ? (
                            <Shimmer width={80} styles={{ root: { margin: '4px 0' } }} />
                        ) : (
                            <span style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>{card.value}</span>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '2px' }}>
                            <span style={{
                                fontSize: '10.5px',
                                color: card.color,
                                fontWeight: 600,
                                opacity: isHovered ? 1 : 0.75,
                                transition: 'opacity 0.15s ease'
                            }}>
                                {card.hint}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default VendorSummaryDashboard;
